import { Link } from 'react-router-dom';
import { LogOut, Shield, Users, History, FileCheck2, ListTree, Search, BarChart3, Languages } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';

interface Props {
  onOpenSearch?: () => void;
}

export default function Header({ onOpenSearch }: Props) {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLanguage();

  return (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-3">
          <img src="/bd-color.svg" alt="BuyDubai" className="h-7 w-auto" />
          <div className="hidden border-l border-gray-200 pl-3 sm:block">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Interní wiki
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-4">
          {user && onOpenSearch && (
            <button
              onClick={onOpenSearch}
              className="hidden items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-teal hover:text-teal md:flex"
              title="Hledat (Cmd/Ctrl+K)"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Hledat</span>
              <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-400">⌘K</kbd>
            </button>
          )}

          {user && (
            <div
              className="hidden items-center rounded-md border border-gray-200 bg-white p-0.5 text-[11px] font-semibold uppercase tracking-wide md:inline-flex"
              role="group"
              aria-label="Jazyk / Language"
              title="Přepnout jazyk"
            >
              <Languages className="ml-1 h-3.5 w-3.5 text-gray-400" />
              <button
                type="button"
                onClick={() => setLang('cs')}
                className={clsx(
                  'rounded px-2 py-1 transition-colors',
                  lang === 'cs' ? 'bg-dark text-white' : 'text-gray-500 hover:text-dark',
                )}
              >
                CS
              </button>
              <button
                type="button"
                onClick={() => setLang('en')}
                className={clsx(
                  'rounded px-2 py-1 transition-colors',
                  lang === 'en' ? 'bg-dark text-white' : 'text-gray-500 hover:text-dark',
                )}
              >
                EN
              </button>
            </div>
          )}
          {user?.role === 'admin' && (
            <nav className="hidden items-center gap-1 md:flex">
              <Link to="/admin/publish" className="btn-ghost">
                <FileCheck2 className="h-4 w-4" /> Publikovat
              </Link>
              <Link to="/admin/readership" className="btn-ghost">
                <BarChart3 className="h-4 w-4" /> Čtenost
              </Link>
              <Link to="/admin/users" className="btn-ghost">
                <Users className="h-4 w-4" /> Uživatelé
              </Link>
              <Link to="/admin/history" className="btn-ghost">
                <History className="h-4 w-4" /> Historie
              </Link>
              <Link to="/admin/audit" className="btn-ghost">
                <ListTree className="h-4 w-4" /> Audit
              </Link>
            </nav>
          )}

          {user && (
            <div className="flex items-center gap-3 pl-3 border-l border-gray-200">
              <div className="text-right">
                <div className="text-sm font-semibold text-dark">{user.name}</div>
                <div className="flex items-center justify-end gap-1 text-[11px] uppercase tracking-wide text-gray-500">
                  {user.role === 'admin' && <Shield className="h-3 w-3" />}
                  {user.role}
                </div>
              </div>
              {user.pictureUrl ? (
                <img src={user.pictureUrl} alt="" className="h-9 w-9 rounded-full" />
              ) : (
                <div className="grid h-9 w-9 place-items-center rounded-full bg-gray-100 font-bold text-gray-600">
                  {user.name[0]?.toUpperCase()}
                </div>
              )}
              <button onClick={logout} className="rounded-md p-2 text-gray-500 hover:bg-gray-100">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
